import React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { StatsCard } from "@/components/stats-card"
import { api } from "@/lib/api"
import { useNotificationStore } from "@/store"
import { Cpu, RefreshCw, Trash2, Zap } from "lucide-react"

export default function CachePage() {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const { data: stats, isLoading } = useQuery({
    queryKey: ["cache-stats"],
    queryFn: () => api.get("/cache/stats"),
  })

  const warmupMutation = useMutation({
    mutationFn: () => api.post("/cache/warmup", { module: "all" }),
    onSuccess: () => {
      queryClient.invalidateQueries(["cache-stats"])
      addNotification({ type: "success", message: "Cache warmed up successfully" })
    },
  })

  const cleanupMutation = useMutation({
    mutationFn: () => api.post("/cache/cleanup"),
    onSuccess: () => {
      queryClient.invalidateQueries(["cache-stats"])
      addNotification({ type: "success", message: "Cache cleaned up successfully" })
    },
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cache Manager</h1>
          <p className="text-muted-foreground">Manage system cache</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => warmupMutation.mutate()} disabled={warmupMutation.isPending}>
            <Zap className="mr-2 h-4 w-4" />
            Warmup
          </Button>
          <Button variant="outline" onClick={() => cleanupMutation.mutate()} disabled={cleanupMutation.isPending}>
            <Trash2 className="mr-2 h-4 w-4" />
            Cleanup
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatsCard title="Total Entries" value={stats?.data?.total || 0} icon={Cpu} />
        <StatsCard title="Active" value={stats?.data?.active || 0} icon={Cpu} />
        <StatsCard title="Expired" value={stats?.data?.expired || 0} icon={Cpu} />
        <StatsCard title="Hit Ratio" value={`${stats?.data?.hit_ratio || 0}%`} icon={Cpu} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cache Modules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {stats?.data?.byModule?.map((module) => (
              <div key={module.module} className="flex items-center justify-between p-3 border rounded-lg">
                <span className="font-medium">{module.module}</span>
                <span className="text-muted-foreground">{module.count} entries</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
